import { useState, forwardRef, ReactNode, ImgHTMLAttributes } from "react";

export type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "onLoad" | "onError"> & {
  /** Image source URL */
  src: string;
  /** Alt text for accessibility */
  alt: string;
  /** Delay before showing the image after load (ms) */
  delay?: number;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for the image element */
  imageClassName?: string;
  /** Custom loading state renderer */
  renderLoading?: () => ReactNode;
  /** Custom error state renderer */
  renderError?: () => ReactNode;
  /** Callback when image loads */
  onLoad?: () => void;
  /** Callback when image fails to load */
  onError?: () => void;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base image component with no styling.
 * Handles loading states, error states, and optional load delay.
 * Use render props to customize loading and error states.
 *
 * @example
 * // In shared/ui/Image.tsx
 * import { ImageBase, type Props as ImageBaseProps } from "@core/ui";
 *
 * export function Image({ className, ...props }: ImageBaseProps) {
 *   return (
 *     <ImageBase
 *       {...props}
 *       className={twMerge("relative", className)}
 *       renderLoading={() => <Loader />}
 *     />
 *   );
 * }
 */
export const ImageBase = forwardRef<HTMLImageElement, Props>(
  (
    {
      src,
      alt,
      delay,
      className,
      imageClassName,
      renderLoading,
      renderError,
      onLoad,
      onError,
      "data-testid": testId = "image",
      ...props
    },
    ref
  ) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const handleLoad = () => {
      if (delay) {
        setTimeout(() => {
          setLoading(false);
          onLoad?.();
        }, delay);
      } else {
        setLoading(false);
        onLoad?.();
      }
    };

    const handleError = () => {
      setLoading(false);
      setError(true);
      onError?.();
    };

    if (error && renderError) {
      return (
        <div className={className} data-testid={testId} data-state="error">
          {renderError()}
        </div>
      );
    }

    return (
      <div className={className} data-testid={testId} data-state={loading ? "loading" : "loaded"}>
        {loading && renderLoading && renderLoading()}
        <img
          ref={ref}
          src={src}
          alt={alt}
          className={imageClassName}
          onLoad={handleLoad}
          onError={handleError}
          data-testid={`${testId}-img`}
          {...props}
        />
      </div>
    );
  }
);

ImageBase.displayName = "ImageBase";

